import { FormEvent, useState } from "react";
import { Edit3, EyeOff, Plus, Send, Trash2 } from "lucide-react";
import { api, json, normalizePage } from "../../lib/api";
import { useAsync } from "../../lib/useAsync";
import { formatDate, StatusBadge } from "../../components/Badge";
import { Field, Input, Select, Textarea } from "../../components/Form";
import {
  Empty,
  ErrorState,
  Failure,
  Loading,
  Pagination,
  Success,
} from "../../components/State";
import type { Page } from "../../lib/types";

interface Announcement {
  id: string;
  category: string;
  status: string;
  version_status: string;
  title: string;
  summary?: string;
  body_markdown: string;
  publication_status?: string;
  is_pinned?: boolean;
  visible_from?: string;
  visible_until?: string;
}
interface Editor {
  id?: string;
  category: string;
  title: string;
  summary: string;
  body_markdown: string;
}
const blank: Editor = {
  category: "official",
  title: "",
  summary: "",
  body_markdown: "",
};
export function AdminAnnouncementsPage() {
  const [page, setPage] = useState(1);
  const result = useAsync(
    async () =>
      normalizePage(
        await api<Page<Announcement>>(
          `/admin/announcements?page=${page}&pageSize=20`,
        ),
      ),
    [page],
  );
  const [editing, setEditing] = useState<Editor>();
  const [publishing, setPublishing] = useState<Announcement>();
  const [visibleFrom, setVisibleFrom] = useState("");
  const [visibleUntil, setVisibleUntil] = useState("");
  const [pinned, setPinned] = useState(false);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    setBusy(true);
    setError("");
    try {
      await api(
        editing.id
          ? `/admin/announcements/${editing.id}`
          : "/admin/announcements",
        { method: editing.id ? "PUT" : "POST", body: json(editing) },
      );
      setEditing(undefined);
      setMessage("公告草稿已保存");
      await result.retry();
    } catch (value) {
      setError(value instanceof Error ? value.message : "保存失败");
    } finally {
      setBusy(false);
    }
  };
  const publish = async (event: FormEvent) => {
    event.preventDefault();
    if (!publishing) return;
    setBusy(true);
    setError("");
    try {
      await api(`/admin/announcements/${publishing.id}/publish`, {
        method: "POST",
        body: json({
          visible_from: visibleFrom ? new Date(visibleFrom).toISOString() : "",
          visible_until: visibleUntil
            ? new Date(visibleUntil).toISOString()
            : "",
          is_pinned: pinned,
          notify,
          sort_weight: pinned ? 100 : 0,
        }),
      });
      setPublishing(undefined);
      setMessage("公告已发布");
      await result.retry();
    } catch (value) {
      setError(value instanceof Error ? value.message : "发布失败");
    } finally {
      setBusy(false);
    }
  };
  const transition = async (
    item: Announcement,
    kind: "offline" | "archive",
  ) => {
    setError("");
    try {
      await api(
        `/admin/announcements/${item.id}${kind === "offline" ? "/offline" : ""}`,
        {
          method: kind === "offline" ? "POST" : "DELETE",
          ...(kind === "offline" ? { body: json({}) } : {}),
        },
      );
      setMessage(kind === "offline" ? "公告已下线" : "公告已归档");
      await result.retry();
    } catch (value) {
      setError(value instanceof Error ? value.message : "操作失败");
    }
  };
  const items = result.data?.items as Announcement[] | undefined;
  return (
    <div>
      <header className="admin-page-header">
        <div>
          <span>ANNOUNCEMENTS</span>
          <h1>公告管理</h1>
          <p>园区公告草稿、版本、发布窗口和下线状态。</p>
        </div>
        <button
          className="primary-button compact"
          onClick={() => setEditing({ ...blank })}
        >
          <Plus size={16} />
          新建公告
        </button>
      </header>
      {message && <Success>{message}</Success>}
      {error && <Failure>{error}</Failure>}
      {result.loading ? (
        <Loading />
      ) : result.error ? (
        <ErrorState error={result.error} retry={result.retry} />
      ) : !items?.length ? (
        <Empty title="暂无公告" />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>公告</th>
                <th>分类</th>
                <th>版本</th>
                <th>发布状态</th>
                <th>可见时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.title}</strong>
                    <small>{item.summary || "-"}</small>
                  </td>
                  <td>{categoryName(item.category)}</td>
                  <td>
                    <StatusBadge status={item.version_status as never} />
                  </td>
                  <td>
                    <StatusBadge
                      status={(item.publication_status || item.status) as never}
                    />
                  </td>
                  <td>{formatDate(item.visible_from)}</td>
                  <td>
                    <div className="row-actions">
                      <button
                        onClick={() =>
                          setEditing({
                            id: item.id,
                            category: item.category,
                            title: item.title,
                            summary: item.summary || "",
                            body_markdown: item.body_markdown,
                          })
                        }
                      >
                        <Edit3 size={14} />
                        编辑新版本
                      </button>
                      {item.version_status === "draft" && (
                        <button
                          onClick={() => {
                            setPublishing(item);
                            setVisibleFrom("");
                            setVisibleUntil("");
                            setPinned(Boolean(item.is_pinned));
                          }}
                        >
                          <Send size={14} />
                          发布
                        </button>
                      )}
                      {item.publication_status === "published" && (
                        <button onClick={() => transition(item, "offline")}>
                          <EyeOff size={14} />
                          下线
                        </button>
                      )}
                      <button
                        className="danger-text"
                        onClick={() => transition(item, "archive")}
                      >
                        <Trash2 size={14} />
                        归档
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination
        page={page}
        pageSize={result.data?.page_size || 20}
        total={result.data?.total || 0}
        onChange={setPage}
      />
      {editing && (
        <div className="modal-layer">
          <button
            className="modal-backdrop"
            aria-label="关闭"
            onClick={() => setEditing(undefined)}
          />
          <form className="modal-card wide-modal" onSubmit={save}>
            <header>
              <h2>{editing.id ? "编辑公告新版本" : "新建公告"}</h2>
              <button type="button" onClick={() => setEditing(undefined)}>
                ×
              </button>
            </header>
            <Field label="分类" required>
              <Select
                value={editing.category}
                onChange={(e) =>
                  setEditing({ ...editing, category: e.target.value })
                }
              >
                <option value="official">官方公告</option>
                <option value="event">活动</option>
                <option value="service">服务</option>
                <option value="maintenance">维护</option>
                <option value="policy_digest">政策摘要</option>
              </Select>
            </Field>
            <Field label="标题" required>
              <Input
                required
                maxLength={200}
                value={editing.title}
                onChange={(e) =>
                  setEditing({ ...editing, title: e.target.value })
                }
              />
            </Field>
            <Field label="摘要">
              <Textarea
                rows={3}
                maxLength={1024}
                value={editing.summary}
                onChange={(e) =>
                  setEditing({ ...editing, summary: e.target.value })
                }
              />
            </Field>
            <Field label="正文 Markdown" required>
              <Textarea
                required
                rows={12}
                maxLength={100000}
                value={editing.body_markdown}
                onChange={(e) =>
                  setEditing({ ...editing, body_markdown: e.target.value })
                }
              />
            </Field>
            <button className="primary-button" disabled={busy}>
              保存草稿
            </button>
          </form>
        </div>
      )}
      {publishing && (
        <div className="modal-layer">
          <button
            className="modal-backdrop"
            aria-label="关闭"
            onClick={() => setPublishing(undefined)}
          />
          <form className="modal-card" onSubmit={publish}>
            <header>
              <h2>发布公告</h2>
              <button type="button" onClick={() => setPublishing(undefined)}>
                ×
              </button>
            </header>
            <Field label="开始可见">
              <Input
                type="datetime-local"
                value={visibleFrom}
                onChange={(e) => setVisibleFrom(e.target.value)}
              />
            </Field>
            <Field label="结束可见">
              <Input
                type="datetime-local"
                value={visibleUntil}
                onChange={(e) => setVisibleUntil(e.target.value)}
              />
            </Field>
            <label className="check-row">
              <input
                type="checkbox"
                checked={pinned}
                onChange={(e) => setPinned(e.target.checked)}
              />
              置顶公告
            </label>
            <label className="check-row">
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
              />
              发送站内通知
            </label>
            <button className="primary-button" disabled={busy}>
              确认发布
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
function categoryName(value: string) {
  return (
    (
      {
        official: "官方公告",
        event: "活动",
        service: "服务",
        maintenance: "维护",
        policy_digest: "政策摘要",
      } as Record<string, string>
    )[value] || value
  );
}
